/* eslint-disable react-hooks/rules-of-hooks */
import { Button, Text } from '@bfe/moly';
import { Dummy } from '@web3/components';
import { div, invoke, web3Navigate } from '@web3/utils';
import clsx from 'clsx';
import { ReactNode, useEffect } from 'react';
import { useLocale } from '../../store/i18nMessage';
import { useCurrentWallet } from '../../store/userWallet';
import { minBybitExtVersion, walletIdMap } from '../../utils/constants';
import { scTrack } from '../../utils/scTrack';
import { DOWNLOAD_LINK } from '../../utils/url';
import { useWalletIsPending } from '../../wallet-hub/helpers/hooks';
import { hasBybitExtension, isRequireBybitExtensionUpdate } from '../../wallet-hub/hubs/BybitExtensionHub';
import { preloadWcConnectProvider } from '../../wallet-hub/hubs/WalletConnectHub';
import { thirdPartyWallet } from '../../wallet-hub/Web3WalletHubCenter';
import bybitExtensionImg from './images/bybit-wallet-extension.svg';
import cloudWalletImg from './images/cloud-wallet.svg';
import fireImg from './images/fire.svg';

export type BybitWalletListProps = {
  onConnect?: () => void;
  noDownLoadApp?: boolean;
  noBybitExtension?: boolean;
  bybitExtVersion?: string;
};

const localScTrack = (spmName: string) => {
  scTrack('Web3userClick', {
    page_name: 'web3sdkBybitWallet',
    spmName,
  });
};
const updateExtDocExtUrl = 'https://bybit-web3.github.io/update-wallet';

export function BybitWalletList({
  onConnect,
  noBybitExtension = false,
  noDownLoadApp = false,
  bybitExtVersion: bybitExtVersionProp,
}: BybitWalletListProps) {
  const { t } = useLocale();

  return (
    <ul className="web3-connect-wallet-dialog-list flex flex-col gap-4 mt-8">
      {/* 插件连接 */}

      {!noBybitExtension && (
        <Dummy>
          {() => {
            // 这其实是一个组件；只是不想写成单独的文件，又想让它有独立的组件状态，共享这个文件里的方法

            const bybitExtVersion = bybitExtVersionProp || minBybitExtVersion;

            const requireUpdate = isRequireBybitExtensionUpdate(bybitExtVersion);

            const { switchToBybitExtension, isThirdParty } = useCurrentWallet();
            const isSwitching = useWalletIsPending();
            // 已经连接和选择了bybit插件，就隐藏按钮
            const hideExtButton = isThirdParty && thirdPartyWallet.isBybitWalletExtension;
            const walletExtButtonText = invoke(() => {
              if (hideExtButton) {
                return '';
              }
              if (!thirdPartyWallet.hasAddress) {
                return t('connectNow');
              }
              return t('switch');
            });

            const handleDownloadBybitExtension = () => {
              localScTrack('DownloadExt');
              const url = thirdPartyWallet.getWalletHub(walletIdMap.bybit)?.installUrl;
              window.open(url);
            };

            const handleUpdateBybitExtension = () => {
              window.open(updateExtDocExtUrl, '_blank');
            };

            const handleConnectBybitWallet = async () => {
              if (isSwitching) {
                return;
              }
              localScTrack('Extension');
              await switchToBybitExtension();
              onConnect?.();
            };

            return rowLayout(
              <img src={bybitExtensionImg} alt="" />,
              <>
                {t('bybitWalletExtension')}
                <img className="size-[14px] ltr:ml-1 rtl:mr-1" src={fireImg} alt="" />
              </>,
              <ul className="list-disc ltr:ml-4 rtl:mr-4">
                <li>{t('easyConnectDapp')}</li>
                <li>{t('supportCloudAndMnemonic')}</li>
              </ul>,
              !hasBybitExtension() ? (
                <Button variant="primary" size="x-small" className="w-full" onClick={handleDownloadBybitExtension}>
                  {t('entry_page_revolution_btn')}
                </Button>
              ) : requireUpdate ? (
                <Button variant="primary" size="x-small" className="w-full" onClick={handleUpdateBybitExtension}>
                  {t('update')}
                </Button>
              ) : hideExtButton ? null : (
                <Button variant="primary" size="x-small" className="w-full" loading={isSwitching} onClick={() => handleConnectBybitWallet()}>
                  {walletExtButtonText}
                </Button>
              ),
              requireUpdate ? <p className="text-bds-brand-700-normal">{t('extUpdateRequired')}</p> : '',
            );
          }}
        </Dummy>
      )}

      {/* app 的wallet connect 连接逻辑 */}
      {!noDownLoadApp && (
        <Dummy>
          {() => {
            // 这其实是一个组件；只是不想写成单独的文件，又想让它有独立的组件状态，以及共享这个文件里的方法
            const { switchToWallet } = useCurrentWallet();
            const thirdConnected = thirdPartyWallet.hasAddress;
            const isSwitching = useWalletIsPending();
            useEffect(() => {
              preloadWcConnectProvider();
            }, []);
            return rowLayout(
              <img src={cloudWalletImg} alt="" />,
              t('bybit_app'),
              <>
                <ul className="list-disc ltr:ml-4 rtl:mr-4">
                  <li>{t('openBybitSwitchWeb3')}</li>
                  <li>{t('scanCodeConnectWallet')}</li>
                </ul>
                <Text.Link
                  href="https://www.bybit.com/zh-MY/download/"
                  className="mt-4 flex items-center text-xs text-white"
                  onClick={() => web3Navigate(DOWNLOAD_LINK)}
                  blank
                >
                  <span>{t('download_bybit')}</span>
                  <span className="inline-flex size-[10px] svg-full ltr:ml-1 rtl:mr-1">{iconRight}</span>
                </Text.Link>
              </>,
              !thirdConnected && (
                <Button
                  className={clsx('w-full', {
                    '!cursor-progress': isSwitching,
                  })}
                  variant="primary"
                  size="x-small"
                  disabled={isSwitching}
                  onClick={async () => {
                    localScTrack('App');
                    await switchToWallet(walletIdMap.walletConnect);
                    onConnect?.();
                  }}
                >
                  {t('getQrCode')}
                </Button>
              ),
            );
          }}
        </Dummy>
      )}
      <li>
        <p className="text-xs text-bds-gray-t2 mt-2">{t('connectCloudWalletInstruction')}</p>
      </li>
    </ul>
  );
}
const iconRight = (
  <svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
    <path d="M25.172 18l-10.586 10.586c-0.781 0.781-0.781 2.047 0 2.828s2.047 0.781 2.828 0l13.986-13.986c0.789-0.789 0.789-2.068 0-2.857l-13.986-13.986c-0.781-0.781-2.047-0.781-2.828 0s-0.781 2.047 0 2.828l10.586 10.586h-23.297c-1.036 0-1.875 0.895-1.875 2s0.84 2 1.875 2h23.297z"></path>
  </svg>
);

const rowLayout = (icon: ReactNode, title: ReactNode, desc: ReactNode, action: ReactNode, titleTip: ReactNode = '') => {
  return (
    <li className={clsx('web3-connect-wallet-dialog-list-item', ' bg-bds-gray-ele-edge flex gap-2 px-4 py-6')}>
      <div>{icon}</div>
      <div className="flex flex-col gap-2 flex-1">
        <div className="flex justify-between">
          <Text className="flex items-center font-semibold" size={18}>
            {title}
          </Text>
          <div className="text-xs text-bds-gray-t2 mt-2">{titleTip}</div>
        </div>
        <div className="flex justify-between gap-2 items-center">
          <div className="text-xs text-bds-gray-t2 mt-2">{desc}</div>
          <div className={clsx('ltr:ml-auto rtl:mr-auto w-[100px]')}>{action}</div>
        </div>
      </div>
    </li>
  );
};
